/**
 * Per-view theming.
 *
 * The ported CSS resolves every colour through a custom property, and a whole
 * theme is one `body.theme-light` block overriding the `:root` dark default.
 * So theming is exactly this: put the right class on <body>.
 *
 * Per-view defaults match the generator — Blueprint dark, Timeline dark,
 * Architecture light.
 *
 * Persistence (T5.5, ruled by the Lead): the choice is kept in localStorage, per
 * view and per DEVICE, not per user. A theme belongs to where you are sitting
 * (dark on a laptop at night, light on a projector), so syncing it through the
 * server would force one choice across both. Nothing but the three theme names
 * is stored: no identity, no token, no plan data. Every read and write is in a
 * try/catch, because storage throws in a private window and with site data
 * blocked, and a display preference must never be able to break the page; an
 * unreadable or malformed value falls back to the generator's defaults.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type Theme = 'dark' | 'light';
/** Canonical view keys, matching the generator's `themeByView`. */
export type ViewKey = 'fp' | 'tl' | 'arch';

const DEFAULT_THEME_BY_VIEW: Record<ViewKey, Theme> = { fp: 'dark', tl: 'dark', arch: 'light' };

interface ThemeState {
  view: ViewKey;
  theme: Theme;
  setView: (view: ViewKey) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'blueprint.theme.byView';

function loadThemes(): Record<ViewKey, Theme> {
  const out = { ...DEFAULT_THEME_BY_VIEW };
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null');
    if (raw && typeof raw === 'object') {
      for (const key of Object.keys(out) as ViewKey[]) {
        const v = (raw as Record<string, unknown>)[key];
        if (v === 'dark' || v === 'light') out[key] = v;
      }
    }
  } catch {
    /* storage blocked or value malformed: the defaults stand */
  }
  return out;
}

function saveThemes(themes: Record<ViewKey, Theme>): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(themes));
  } catch {
    /* private window or blocked storage: the choice lasts for this session only */
  }
}

const ThemeContext = createContext<ThemeState | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [view, setView] = useState<ViewKey>('fp');
  const [byView, setByView] = useState<Record<ViewKey, Theme>>(loadThemes);

  const theme = byView[view];

  // The single side effect: the body class the ported CSS keys off.
  useEffect(() => {
    const { body } = document;
    body.classList.remove('theme-dark', 'theme-light');
    body.classList.add(`theme-${theme}`);
  }, [theme]);

  // Written from the handlers, not an effect, so merely loading never writes.
  const update = useCallback(
    (fn: (prev: Record<ViewKey, Theme>) => Record<ViewKey, Theme>) =>
      setByView((prev) => {
        const next = fn(prev);
        saveThemes(next);
        return next;
      }),
    [],
  );

  const setTheme = useCallback(
    (next: Theme) => update((prev) => ({ ...prev, [view]: next })),
    [view, update],
  );

  const toggleTheme = useCallback(
    () => update((prev) => ({ ...prev, [view]: prev[view] === 'dark' ? 'light' : 'dark' })),
    [view, update],
  );

  const value = useMemo<ThemeState>(
    () => ({ view, theme, setView, setTheme, toggleTheme }),
    [view, theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside a ThemeProvider');
  return context;
}
